/**
 * cas-1b94 — avatar decisions for social profile cards.
 *
 * Instagram hands us `profile_pic_url: null` for plenty of accounts, and its
 * CDN URLs are signed and expire, so any card showing a profile picture needs
 * a deliberate answer for "no usable URL". Keeping that answer here (rather
 * than inline in an SFC) makes it unit-testable — this project cannot mount
 * Quasar components under Vitest.
 */

export interface ProfileAvatarIdentity {
  username?: string | null;
  fullName?: string | null;
}

/**
 * Normalises a profile-picture URL into something safe to hand to
 * <SmartImage> (which proxies Instagram/TikTok CDN URLs through the public
 * /api/v1/posts/proxy-image endpoint), or null when the card should render an
 * initial-letter avatar instead of a broken image.
 */
export function resolveAvatarImageUrl(url?: string | null): string | null {
  if (typeof url !== 'string') return null;

  const trimmed = url.trim();
  if (!trimmed) return null;

  const isFetchable =
    trimmed.startsWith('https://') ||
    trimmed.startsWith('http://') ||
    trimmed.startsWith('data:image/') ||
    trimmed.startsWith('/');

  return isFetchable ? trimmed : null;
}

/**
 * The letter shown in the fallback avatar. Prefers the handle, falls back to
 * the display name, and never returns an empty string — Instagram handles may
 * start with '.' or '_' and may consist only of punctuation once trimmed.
 */
export function profileAvatarInitial(profile?: ProfileAvatarIdentity | null): string {
  for (const candidate of [profile?.username, profile?.fullName]) {
    if (typeof candidate !== 'string') continue;

    const trimmed = candidate.trim();
    if (!trimmed) continue;

    const firstAlphanumeric = Array.from(trimmed).find(character =>
      /[\p{L}\p{N}]/u.test(character)
    );
    if (firstAlphanumeric) return firstAlphanumeric.toUpperCase();
  }

  return '@';
}
